import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectGodown from "components/ui/selectors/SelectGodown";
import { useAllAgitemdetails } from "http-hooks/agitemdetail.js/useAgitemdetail";
import { useGenerateStockReport } from "http-hooks/stockreport/useStockReport";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

const reportTypeOptions = [
  { value: "stock-register", label: "Stock Register" },
  { value: "closing-stock", label: "Closing Stock" },
  { value: "item-ledger", label: "Item Stock Ledger" },
  { value: "stock-valuation", label: "Stock Valuation" },
  { value: "valuation-compare", label: "Valuation Comparison" },
];

const valuationMethodOptions = [
  { value: "average-cost", label: "Weighted Average" },
  { value: "fifo", label: "FIFO" },
  { value: "last-purchase-cost", label: "Last Purchase Cost" },
];

export default function StockReport() {
  const navigate = useNavigate();
  const { data: itemDetails = [], isLoading: isLoadingItems } = useAllAgitemdetails();
  const { mutateAsync: generateStockReport, isPending } = useGenerateStockReport();
  const [formData, setFormData] = useState({
    reportType: "stock-register",
    valuationMethod: "average-cost",
    fromDate: "",
    asOfDate: "",
    godown: { value: 0, label: "All Godowns" },
    item: null,
  });
  const isValuation = formData.reportType === "stock-valuation";
  const isCompare = formData.reportType === "valuation-compare";

  const itemOptions = useMemo(
    () =>
      (Array.isArray(itemDetails) ? itemDetails : itemDetails?.data || []).map((item) => ({
        value: item.agitemdtlId,
        label: [
          item.agitemmast?.brand,
          item.agitemmast?.subbrandname,
          item.agitemmast?.itemCode,
          item.agitemmast?.itemName,
          item.shp,
          item.clt,
          item.sz,
          item.szuom,
        ]
          .filter(Boolean)
          .join(" "),
      })),
    [itemDetails]
  );

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formData.reportType === "item-ledger" && !formData.item) {
      return;
    }

    try {
      const data = await generateStockReport({
        reportType: formData.reportType,
        valuationMethod: formData.valuationMethod,
        fromDate: formData.fromDate,
        asOfDate: formData.asOfDate,
        godownId: formData.godown?.value || 0,
        itemId: formData.item?.value || "",
      });
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);
      navigate(
        getDocumentViewerRoute("stock-report", {
          htmlUrl,
          pdfUrl,
          documentNumber: formData.asOfDate,
          documentType: reportTypeOptions.find((item) => item.value === formData.reportType)?.label,
        })
      );
    } catch (error) {
      console.error("Unable to generate stock report:", error);
    }
  };

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold text-gray-900">Stock Reports</h1>
      <div className="mt-8 max-w-4xl rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Select
            label="Report Type *"
            value={reportTypeOptions.find((item) => item.value === formData.reportType)}
            onChange={(value) => updateField("reportType", value?.value || "stock-register")}
            options={reportTypeOptions}
            isClearable={false}
          />
          <SelectGodown
            label="Godown"
            value={formData.godown}
            onChange={(value) => updateField("godown", value || { value: 0, label: "All Godowns" })}
          />
          <Input
            label="Period Start *"
            type="date"
            value={formData.fromDate}
            onChange={(event) => updateField("fromDate", event.target.value)}
            fys
            withinFY
            required
          />
          <Input
            label="As On Date *"
            type="date"
            value={formData.asOfDate}
            onChange={(event) => updateField("asOfDate", event.target.value)}
            now
            withinFY
            required
          />
          {isValuation && (
            <div className="sm:col-span-2">
              <Select
                label="Valuation Method *"
                value={valuationMethodOptions.find((item) => item.value === formData.valuationMethod)}
                onChange={(value) => updateField("valuationMethod", value?.value || "average-cost")}
                options={valuationMethodOptions}
                isClearable={false}
                isSearchable
              />
            </div>
          )}
          {isCompare && (
            <p className="sm:col-span-2 text-sm text-gray-600">
              Same closing quantity, valued by Weighted Average, FIFO, and Last Purchase Cost.
            </p>
          )}
          {formData.reportType === "item-ledger" && (
            <div className="sm:col-span-2">
              <Select
                label="Item Variant *"
                value={formData.item}
                onChange={(value) => updateField("item", value)}
                options={itemOptions}
                isLoading={isLoadingItems}
                isClearable
                isSearchable
                placeholder="Select an item variant"
              />
            </div>
          )}
          <div className="sm:col-span-2 flex justify-center">
            <button
              type="submit"
              disabled={isPending || (formData.reportType === "item-ledger" && !formData.item)}
              className="rounded-md bg-indigo-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
